import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { RemediationItemSummary } from '@libs/types';
import { Box, Button, Chip, type SxProps, type Theme, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { CalloutBanner } from '@components/CalloutBanner';
import { ListPagination } from '@components/ListPagination';
// Deep import on purpose: `@features/assessment` re-exports a *query* hook of
// the same name from assessment-documents-api, while the click-to-download
// mutation lives here. DomainResultItem imports it the same way.
import { useGetDocumentDownloadUrl } from '@features/assessment/api/documents';
import { RemediationItemStatus, RemediationItemType, RemediationRunStatus } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';
import {
  ArrowCounterClockwiseIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  ClockIcon,
  DownloadIcon,
  EyeIcon,
  MinusCircleIcon,
  PlusIcon,
  SparkleIcon,
  SpinnerIcon,
  TrashIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';

import { useRemediationRun, useResolveFailedRemediationItem } from '../api/remediation-api';
import { RemediationRunSkeleton } from '../components/RemediationSkeletons';
import {
  DocumentListCard,
  DocumentListHeader,
  DocumentPipeline,
  ElapsedTime,
  FileTypeTile,
  RUN_ITEM_GRID,
  RUN_ITEM_ROW_SX,
  RunHero,
  RunStats,
} from '../components/RunProgressParts';
import { itemErrorKey } from '../utils/item-error';
import {
  computeItemProgress,
  elapsedMinutes,
  FINISHED_ITEM_STATUSES,
  foldProgressCounts,
  isOpenableInReview,
  type ItemProgress,
  REVIEWED_ITEM_STATUSES,
  splitElapsed,
} from '../utils/run-progress';

/**
 * Rows per page. Ten keeps the card roughly the height of the summary above it,
 * so the two read as one screen rather than a list that runs off the bottom.
 */
const DOCUMENTS_PER_PAGE = 10;

/**
 * Screens 5 + 7 — the run's own page.
 *
 * One route rather than two: "generating" and "finished" are states of the same
 * resource, the run query self-polls, and the content swaps underneath.
 */
export const RemediationProgressPage: FC = observer(() => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { t } = useTrans();
  const [page, setPage] = useState(1);
  // Server-paginated: the endpoint returns this page's documents plus whole-run
  // counts. It used to return every item and this screen sliced to ten — on a
  // route that polls every few seconds, so a run with dozens of documents
  // re-downloaded all of them each tick to render a tenth.
  const {
    data: run,
    isLoading,
    isFetching,
    isError,
  } = useRemediationRun(runId, { page, limit: DOCUMENTS_PER_PAGE });
  const download = useGetDocumentDownloadUrl();
  const resolveFailed = useResolveFailedRemediationItem(runId ?? '');
  const now = useTicker(
    run?.status === RemediationRunStatus.PENDING || run?.status === RemediationRunStatus.PROCESSING,
  );

  // Clamped rather than reset, and only when the page has actually run off the
  // end: the document list grows while a run works, so a reviewer who paged
  // ahead must not be dragged back to page 1 by the next poll. Overshooting
  // matters because the server answers a page past the end with no rows at all,
  // where the old client-side slice silently showed the last page.
  const totalItemPages = run?.itemsMeta?.totalPages;
  useEffect(() => {
    if (totalItemPages !== undefined && page > totalItemPages) {
      setPage(Math.max(1, totalItemPages));
    }
  }, [page, totalItemPages]);

  // One step back along the journey — scope, run, review, placeholders,
  // summary — not out of it. Back used to land on the runs table, which is
  // where the journey *starts* rather than where the user just was, so it read
  // as "leave" on every screen that offered it. The picker is the step before
  // this one, and it is also where more documents get added.
  const back = () =>
    navigate(run ? routes.remediationScope(run.assessmentId) : routes.remediationList());

  if (isLoading || (!run && !isError)) {
    return (
      <Shell>
        <RemediationRunSkeleton />
      </Shell>
    );
  }

  if (isError || !run) {
    return (
      <Shell onBack={back}>
        <Box sx={{ p: 3 }}>
          <CalloutBanner tone="error" title={t('app.remediation.progress.loadError')} />
        </Box>
      </Shell>
    );
  }

  // This page's rows only. Everything above them is whole-run — see below.
  const pageItems = run.items;
  const itemsMeta = run.itemsMeta;
  const totalItems = run.itemCounts.total;
  const working =
    run.status === RemediationRunStatus.PENDING || run.status === RemediationRunStatus.PROCESSING;

  const progressByItem = pageItems.map((item) =>
    computeItemProgress({
      status: item.status,
      runStatus: run.status,
      aiStartedAt: item.aiStartedAt,
    }),
  );

  // Whole-run, from the server's per-status tally — never from the rows on
  // screen, which are one page. Still counted off the progress states rather
  // than off raw statuses, so every tile moves as each document lands instead of
  // all at once at the end of the run; `foldProgressCounts` applies the same
  // rules `computeItemProgress` applies per row.
  const { failed, processing, queued, done } = foldProgressCounts(run.itemStatusCounts);

  // Failures the callout can NAME, which is only ever the ones on this page —
  // `items` is one page now. The count it leads with is the whole-run figure from
  // the tally, so the notice still raises every failure on the run; what it can
  // spell out is best-effort. See `FailedDocumentsCallout` for how it words the
  // difference.
  const failedItems = pageItems.filter(
    (_item, index) => progressByItem[index].state === 'failed',
  );

  // Measured, not projected — see run-progress.ts for why there is no estimate.
  const elapsed = splitElapsed(elapsedMinutes(run.createdAt, now));

  const reviewable =
    run.status === RemediationRunStatus.READY_FOR_REVIEW ||
    run.status === RemediationRunStatus.PROCESSING;

  // A finished run's documents are folded into a new version, or abandoned;
  // neither can be edited after the fact, and the endpoint refuses it too.
  const terminalRun =
    run.status === RemediationRunStatus.COMPLETED ||
    run.status === RemediationRunStatus.DISCARDED ||
    run.status === RemediationRunStatus.FAILED;

  // Where more documents can still join this run. The endpoint itself only
  // refuses a terminal run, but FINALIZING is left out deliberately: by then the
  // approved documents are being rendered and the next assessment version
  // forked, and appending would start a second execution alongside that. The
  // honest offer at that point is a new run, which the scope page makes once
  // this one lands.
  const canAddDocuments =
    run.status === RemediationRunStatus.PENDING ||
    run.status === RemediationRunStatus.PROCESSING ||
    run.status === RemediationRunStatus.READY_FOR_REVIEW;

  // Only the rows are paged. Every count above them — the hero, the stat tiles,
  // the card's own total — stays whole-run, because they are the summary the
  // reviewer came back for and a per-page summary would answer a question
  // nobody asked.
  const totalPages = Math.max(1, itemsMeta?.totalPages ?? 1);
  const currentPage = itemsMeta?.currentPage ?? page;
  const pageStart = (currentPage - 1) * DOCUMENTS_PER_PAGE;
  const pageRows = pageItems.map((item, index) => ({ item, progress: progressByItem[index] }));

  return (
    <Shell>
      <RunHero
        status={run.status}
        done={done}
        processing={processing}
        total={totalItems}
        onBack={back}
        trailing={working ? <ElapsedTime {...elapsed} /> : undefined}
      />

      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 5 }, py: 3 }}>
        {/* Full width, as in the design: the document rows carry a pipeline bar
            and a per-row action, and a centred column starves both. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          {run.status === RemediationRunStatus.READY_FOR_REVIEW && (
            <CalloutBanner
              tone="ai"
              filledIcon
              title={t('app.remediation.progress.readyTitle')}
              action={
                <Button
                  variant="primary"
                  endIcon={<ArrowRightIcon size={16} />}
                  onClick={() => navigate(routes.remediationReview(run.runId))}
                >
                  {t('app.remediation.progress.reviewNow')}
                </Button>
              }
            >
              {t('app.remediation.progress.readyBody')}
            </CalloutBanner>
          )}

          {run.status === RemediationRunStatus.COMPLETED && (
            <CalloutBanner
              tone="success"
              title={t('app.remediation.final.title')}
              action={
                run.resultAssessmentId ? (
                  /* Straight into the new version's workflow, the same place
                     "Start New Version" lands: it is a draft the user reviews
                     and submits themselves, so there is nothing to wait for. */
                  <Button
                    variant="primary"
                    endIcon={<ArrowRightIcon size={16} />}
                    onClick={() =>
                      navigate(routes.assessmentDomain(run.resultAssessmentId as string, 1))
                    }
                  >
                    {t('app.remediation.final.openAssessment')}
                  </Button>
                ) : undefined
              }
            >
              {t('app.remediation.final.bodyNoVersion')}
            </CalloutBanner>
          )}

          {/* Discarded and failed runs leave the origin assessment untouched and
              latest, so its selection page is still the place to start again. */}
          {run.status === RemediationRunStatus.DISCARDED && (
            <CalloutBanner
              tone="neutral"
              title={t('app.remediation.final.discardedTitle')}
              action={<RemediateAgainButton assessmentId={run.assessmentId} />}
            >
              {/* The run's own reason can be an AI or infrastructure string;
                  it belongs in logs, not on screen. */}
              {t('app.remediation.final.discardedBody')}
            </CalloutBanner>
          )}

          {run.status === RemediationRunStatus.FAILED && (
            <CalloutBanner
              tone="error"
              title={t('app.remediation.progress.failedTitle')}
              action={<RemediateAgainButton assessmentId={run.assessmentId} />}
            >
              {t('app.remediation.progress.failedBody')}
            </CalloutBanner>
          )}

          {/* Which documents failed, and why, the moment each one does.

              This used to be a bare count, and it was hidden entirely once the
              run itself ended FAILED — so the case where every document failed,
              the one where the reasons matter most, replaced them with "we could
              not finish this run". The reason is on the row too, but the row can
              be on page two of a list the user has to go looking through.

              It stays alongside the run-level notice rather than instead of it:
              one says the run is over, this says what to do about it. */}
          {failed > 0 && <FailedDocumentsCallout items={failedItems} count={failed} />}

          {/* Shown for every run state, not just while working: after the run
              finishes the counts are the result, and hiding them would empty
              the page of the summary the user came back for. */}
          <RunStats done={done} processing={processing} queued={queued} failed={failed} />

          <DocumentListCard
            title={t('app.remediation.progress.documents')}
            count={t('app.remediation.progress.itemsCount', { count: totalItems })}
            /* Back to the picker rather than into a dialog of its own. Choosing
               documents is what that screen is for, and it already knows how to
               do it against a run in flight: it drops what the run holds, ticks
               nothing, and its footer appends instead of starting a second run.
               A dialog here was a second, smaller copy of that screen. */
            action={
              canAddDocuments ? (
                <Button
                  variant="tertiary"
                  startIcon={<PlusIcon size={15} />}
                  onClick={() => navigate(routes.remediationScope(run.assessmentId))}
                >
                  {t('app.remediation.addDocs.open')}
                </Button>
              ) : undefined
            }
            header={
              <DocumentListHeader
                labels={[
                  t('app.remediation.progress.colDocument'),
                  t('app.remediation.progress.colDomain'),
                  t('app.remediation.progress.colStatus'),
                  t('app.remediation.progress.colProgress'),
                  '',
                ]}
              />
            }
            /* A run can hold dozens of documents, and the page's own summary
               lives above the list — so the list is paged rather than made
               endlessly scrollable past it. The counts, the hero and the
               stat tiles all stay whole-run: only the rows are a page. */
            footer={
              totalPages > 1 ? (
                <ListPagination
                  page={currentPage}
                  totalPages={totalPages}
                  hasPrevPage={itemsMeta?.hasPrevPage}
                  hasNextPage={itemsMeta?.hasNextPage}
                  // `isFetching`, not `isLoading`: the rows stay on screen while
                  // the next page loads, so the pager is the thing that has to
                  // say a request is in flight.
                  isLoading={isFetching}
                  onPageChange={setPage}
                  sx={{ mt: 0 }}
                  summary={t('app.remediation.progress.pageRange', {
                    from: pageStart + 1,
                    to: pageStart + pageRows.length,
                    total: totalItems,
                  })}
                />
              ) : undefined
            }
          >
            {pageRows.map(({ item, progress }) => (
              <RunItemRow
                key={item.itemId}
                item={item}
                runStatus={run.status}
                progress={progress}
                now={now}
                /* Every document the reviewer can open, not just the undecided
                   ones: a decision is changeable right up to finalize, so a
                   document that has been reviewed still needs a way back into
                   the review screen. A failed one has nothing to show there,
                   and neither does one the AI is still writing or has written
                   but not yet delivered — both sit at PROCESSING with no
                   content, which is exactly what the row's own state says. */
                onReview={
                  reviewable && isOpenableInReview(item.status)
                    ? () => navigate(routes.remediationReview(run.runId, item.itemId))
                    : undefined
                }
                onDownload={
                  item.draftDocumentId
                    ? () => download.mutate({ documentId: item.draftDocumentId as string })
                    : undefined
                }
                /* A failed document is not a result — it is one the tenant
                   asked for and did not get, with no content and no decision
                   to make. Offered only while the run can still act on it. */
                onResolveFailure={
                  progress.state === 'failed' && !terminalRun
                    ? (action) =>
                        resolveFailed.mutate({ runId: run.runId, itemId: item.itemId, action })
                    : undefined
                }
                resolving={resolveFailed.isPending}
              />
            ))}
          </DocumentListCard>

          {working && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
              <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                {t('app.remediation.progress.leave')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Shell>
  );
});

/**
 * How many failed documents to name before the list stops being a list.
 *
 * A run where everything failed is a real case — a missing upstream artifact
 * fails every document in the batch for the same reason — and printing eleven
 * identical sentences buries the one thing the user needs to read.
 */
const NAMED_FAILURES = 4;

/**
 * The failed documents, named, with the reason each one gives.
 *
 * Never the AI's own text: it carries raw ids and internal artifact names. The
 * wire error code picks a message written for a user, and a document with no
 * recognised code is listed by name alone rather than with a guess.
 */
const FailedDocumentsCallout: FC<{ items: RemediationItemSummary[]; count: number }> = observer(
  ({ items, count }) => {
    const { t } = useTrans();
    const named = items.slice(0, NAMED_FAILURES);
    // `count` is whole-run while `items` only reaches as far as the caller's page,
    // so this can be every failure on the run. Suppressed when nothing is named:
    // "3 documents failed" followed by "and 3 more failed" reads as six.
    const hidden = named.length > 0 ? count - named.length : 0;

    return (
      <CalloutBanner
        tone="warning"
        filledIcon
        title={t('app.remediation.progress.someFailed', { count })}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
          {named.map((item) => {
            const errorKey = itemErrorKey(item);
            return (
              <Typography key={item.itemId} variant="textXS_regular" sx={{ display: 'block' }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {item.documentName ?? t(`app.remediation.itemType.${item.type}` as SingularKeys)}
                </Box>
                {errorKey ? ` — ${t(errorKey)}` : ''}
              </Typography>
            );
          })}
          {hidden > 0 && (
            <Typography variant="textXS_regular" sx={{ display: 'block', color: 'text.secondary' }}>
              {t('app.remediation.progress.andMoreFailed', { count: hidden })}
            </Typography>
          )}
        </Box>
      </CalloutBanner>
    );
  },
);

/** Returns to the document selection so the user can pick a different subset. */
const RemediateAgainButton: FC<{ assessmentId: string }> = observer(({ assessmentId }) => {
  const navigate = useNavigate();
  const { t } = useTrans();
  return (
    <Button variant="secondary" onClick={() => navigate(routes.remediationScope(assessmentId))}>
      {t('app.remediation.final.remediateMore')}
    </Button>
  );
});

/**
 * The catalog domain a document belongs to, as the raw key the scope screen
 * already tags rows with (`OMs`, …). Deliberately not translated: the catalog
 * ships domain names as localized content the run payload does not carry, and
 * the key is what the rest of the remediation flow shows.
 */
const DomainTag: FC<{ domainKey: string; sx?: SxProps<Theme> }> = observer(({ domainKey, sx }) => {
  const theme = useTheme();
  return (
    <Chip
      size="small"
      variant="outlined"
      label={domainKey}
      title={domainKey}
      sx={{
        height: 20,
        maxWidth: '100%',
        fontSize: 11,
        fontWeight: 600,
        color: 'text.secondary',
        borderColor: theme.palette.grey[400],
        ...sx,
      }}
    />
  );
});

/**
 * One document in the run: file tile, operation chip, its real state, a bar that
 * is indeterminate while the AI is writing it, and — once its content has landed
 * — a Review action and a download.
 *
 * Nothing here is interpolated. Every visual comes from something the run
 * actually reported: the item's status, and the per-document start/finish the
 * Job posts as it works. See `run-progress` for why there is still no stage
 * breakdown or countdown.
 */
const RunItemRow: FC<{
  item: RemediationItemSummary;
  runStatus: RemediationRunStatus;
  progress: ItemProgress;
  /** Ticking clock, for how long the AI has been on this document. */
  now: number;
  onReview?: () => void;
  onDownload?: () => void;
  /** Retry or drop a document the AI could not produce. Failed items only. */
  onResolveFailure?: (action: 'retry' | 'remove') => void;
  resolving?: boolean;
}> = observer(
  ({ item, runStatus, progress, now, onReview, onDownload, onResolveFailure, resolving }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const isGenerate = item.type === RemediationItemType.GENERATE;
  const isFailed = progress.state === 'failed';
  const errorKey = itemErrorKey(item);
  const settled = FINISHED_ITEM_STATUSES.includes(item.status);
  // A decision has actually been made. Narrower than `settled`, which counts
  // REVIEW_PENDING as done — true of the AI's work, but it is the opposite of
  // reviewed, and it used to be labelled as such once a run left review.
  const reviewed = REVIEWED_ITEM_STATUSES.includes(item.status);
  const runWorking =
    runStatus === RemediationRunStatus.PENDING || runStatus === RemediationRunStatus.PROCESSING;

  // The ways a document can be finished are different answers, so they stop
  // sharing one green tick: accepted is the AI's work kept, rejected is the
  // original kept — grey, the language the review rail's pins and decision
  // icons already use for a rejection — and "no changes needed" is neither
  // decision, a document the AI found nothing to improve.
  //
  // These three have to be read off the item's own status, because
  // `progress.state` collapses all of them into `done` — the right answer for
  // the pipeline bar, which reports the AI's work, and the wrong one for an
  // outcome the reviewer decided.
  const outcome = isFailed
    ? { color: theme.palette.error.main, icon: WarningIcon }
    : item.status === RemediationItemStatus.REJECTED
      ? { color: theme.palette.text.secondary, icon: XCircleIcon }
      : item.status === RemediationItemStatus.NO_CHANGES
        ? { color: theme.palette.blue[700], icon: MinusCircleIcon }
        : progress.state === 'done'
          ? { color: theme.palette.green[700], icon: CheckIcon }
          : progress.state === 'processing'
            ? { color: theme.palette.ocean[700], icon: SpinnerIcon }
            : { color: theme.palette.text.tertiary, icon: ClockIcon };

  const statusColor = outcome.color;
  const StatusIcon = outcome.icon;

  // Straight off the reported state now that there is one. The one inference left
  // is the old one, and only for a run reporting no per-item progress at all: a
  // PENDING item on a live run is somewhere in the queue, and there is no stage
  // name to show because the AI reports no stages.
  const statusKey: SingularKeys =
    runWorking && !settled && progress.state !== 'queued'
      ? 'app.remediation.itemStatus.processing'
      : (`app.remediation.itemStatus.${item.status}` as SingularKeys);

  // Measured from the moment the AI picked this document up — the only per-
  // document duration there is. Sub-minute readings say nothing, so they wait.
  const itemMinutes =
    progress.state === 'processing' && progress.startedAt
      ? elapsedMinutes(progress.startedAt, now)
      : 0;

  return (
    <Box
      sx={{
        ...RUN_ITEM_ROW_SX,
        py: 2,
        display: 'grid',
        // Shared with the header strip — see RUN_ITEM_GRID for why the tracks
        // are shaped the way they are.
        gridTemplateColumns: RUN_ITEM_GRID,
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        '&:last-of-type': { borderBottom: 'none' },
        backgroundColor: isFailed ? theme.palette.orange[100] : 'transparent',
        borderInlineStart: `3px solid ${isFailed ? theme.palette.error.main : 'transparent'}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
        <FileTypeTile fileName={item.documentName} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="textSM_regular"
            sx={{
              fontWeight: 500,
              // The app's custom typography variants have no `variantMapping`, so
              // MUI renders them as <span>. `noWrap` was therefore only applying
              // `white-space: nowrap` — its `overflow: hidden` and ellipsis have
              // no effect on an inline box — and a long file name ran straight
              // out of its column and under the domain tag. Blocks wrap.
              display: 'block',
              // A file name is one unbroken token: without this it would refuse
              // to wrap and overflow exactly as it does now.
              overflowWrap: 'anywhere',
            }}
          >
            {item.documentName ?? t(`app.remediation.itemType.${item.type}` as SingularKeys)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', mt: 0.6 }}>
            <Chip
              size="small"
              icon={isGenerate ? <SparkleIcon size={11} weight="fill" /> : undefined}
              label={t(
                isGenerate
                  ? 'app.remediation.progress.opGenerate'
                  : 'app.remediation.progress.opRewrite',
              )}
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: isGenerate ? theme.palette.ocean[100] : theme.palette.blue[100],
                color: isGenerate ? theme.palette.ocean[700] : theme.palette.blue[700],
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
            {/* Narrow layouts have no domain column — see the grid comment. */}
            {item.domainKey && (
              <DomainTag domainKey={item.domainKey} sx={{ display: { lg: 'none' } }} />
            )}
          </Box>
          {/* Why this document failed, beside the document it is about.
              It used to sit in the progress column under the bar, in tertiary
              grey and on one truncated line — which is where a duration goes,
              not a reason: the sentence that tells a user their file was
              unreadable was clipped mid-word and coloured like an aside. Here
              it wraps, and it carries the error colour the row's icon and bar
              already use.

              Never the AI's own text: it carries raw ids and internal artifact
              names. The wire error code picks a message written for a user. */}
          {errorKey && (
            <Typography
              variant="textXS_regular"
              sx={{ color: 'error.main', mt: 0.6, display: 'block' }}
            >
              {t(errorKey)}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', minWidth: 0 }}>
        {item.domainKey && <DomainTag domainKey={item.domainKey} />}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StatusIcon size={14} color={statusColor} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: statusColor }} noWrap>
          {t(statusKey)}
        </Typography>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <DocumentPipeline progress={progress} />
        <Typography
          variant="textXS_regular"
          sx={{ color: 'text.tertiary', mt: 0.75, display: 'block' }}
          noWrap
        >
          {/* The only honest thing to put under the bar is measured: how long
              this document has been going. Never an estimate — this used to hold
              a "~N min left" countdown extrapolated from elapsed time, which
              nothing supports. A failure reason is not a duration; it now sits
              beside the document name instead. */}
          {itemMinutes > 0
            ? t('app.remediation.progress.itemElapsed', { minutes: itemMinutes })
            : ''}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          justifyContent: 'flex-end',
          alignItems: 'center',
          // The track is fixed, so a longer label (or the rare row that offers
          // two actions) wraps inside the column rather than pushing it wider.
          flexWrap: 'wrap',
          minWidth: 0,
        }}
      >
        {/* Review is per document: the reviewer can start on the first one that
            lands rather than waiting for the whole batch. Once a document has
            been decided the same control stays put and turns green — the state
            of one thing, not two different things — and it keeps opening the
            review screen, because a decision can be changed until finalize. */}
        {!reviewed && onReview && (
          <Button variant="secondary" onClick={onReview} startIcon={<EyeIcon size={16} />}>
            {t('app.remediation.progress.reviewDocument')}
          </Button>
        )}
        {onDownload && (
          <Button variant="tertiary" onClick={onDownload} startIcon={<DownloadIcon size={16} />}>
            {t('app.remediation.final.download')}
          </Button>
        )}
        {/* Still a marker once the run has left review: there is nothing to
            reopen then, so it renders as a `div` with no pointer events rather
            than as a button that would do nothing when clicked. */}
        {/* The two ways out of a failure. Retry first: most failures are
            transient — a timeout, a model hiccup, an upstream artifact that
            arrived late — so the common answer is "try that again". Remove is
            for the document the tenant has decided against, or one that fails
            for a reason no retry will fix. Without these the only way out was
            to abandon the whole run, throwing away every decision already made
            on the documents that did work. */}
        {onResolveFailure && (
          <>
            <Button
              variant="secondary"
              disabled={resolving}
              onClick={() => onResolveFailure('retry')}
              startIcon={<ArrowCounterClockwiseIcon size={15} />}
            >
              {t('app.remediation.progress.retryDocument')}
            </Button>
            <Button
              variant="tertiary"
              disabled={resolving}
              onClick={() => onResolveFailure('remove')}
              startIcon={<TrashIcon size={15} />}
            >
              {t('app.remediation.progress.removeDocument')}
            </Button>
          </>
        )}
        {!onDownload && reviewed && !isFailed && (
          <Button
            component={onReview ? 'button' : 'div'}
            variant="secondary"
            onClick={onReview}
            disableRipple={!onReview}
            startIcon={<CheckIcon size={16} />}
            sx={{
              backgroundColor: theme.palette.green[200],
              color: theme.palette.green[800],
              ...(onReview
                ? { '&:hover': { backgroundColor: theme.palette.green[300] } }
                : {
                    pointerEvents: 'none',
                    '&:hover': { backgroundColor: theme.palette.green[200] },
                  }),
            }}
          >
            {t('app.remediation.progress.reviewedDocument')}
          </Button>
        )}
      </Box>
    </Box>
  );
});

/**
 * Page frame. The back control is NOT here: on the loaded page it lives inside
 * the hero so it shares the hero's background, and a strip of its own would
 * open a band of bare white above the header. The loading and error branches
 * pass it explicitly, since they have no hero to put it in.
 */
const Shell: FC<{
  onBack?: () => void;
  children: React.ReactNode;
}> = observer(({ onBack, children }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'background.paper',
      borderRadius: 4,
      overflow: 'hidden',
    }}
  >
    {onBack && (
      <Box sx={{ px: 2, pt: 2 }}>
        <Button variant="tertiary" onClick={onBack} sx={{ minWidth: 0, px: 1.25 }}>
          <ArrowLeftIcon size={18} />
        </Button>
      </Box>
    )}
    {children}
  </Box>
));

/**
 * Wall clock that advances on its own.
 *
 * The run query polls, but the elapsed pill counts real time rather than
 * responses — without its own tick it would only move when the server was asked
 * something, and would sit frozen between polls. Stops as soon as the run does.
 */
function useTicker(active: boolean, everyMs = 5_000): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [active, everyMs]);

  return now;
}
